import { ButtonConfig } from '@zipari/shared-ds-util-button';
import { ModelAttributeConfig } from '@zipari/shared-ds-util-configuration';
import { AllControlsConfiguration } from '@zipari/shared-ds-util-form';
import { MessageBannerConfig } from '@zipari/shared-ds-util-messages';
import { FormGroupArrayConfig } from '../../chords/form-group/form-group-array/form-group-array.config';
import { InfoPanel } from '../../chords/info-panel/info-panel.model';
import { IconConfig } from '../../notes/icon/icon.model';
import { AccordionConfiguration } from '../../shared/interfaces/accordion.inteface';

export interface ClaimSubmissionTabLabelConfig {
  pill?: number;
  text: string;
  subHeading?: string;
}

export interface Option {
  label: string;
  value: string;
}

export interface ClaimsSubmissionConfig {
  submitClaims: SubmitClaimsConfig;
  claimsInformation?: ClaimSubmissionInformationConfig;
  confirmationPage?: ConfirmationPageConfig;
  mobileHeader?: AccordionConfiguration;
}

export interface SubmitClaimsConfig {
  medicalClaim: ClaimSubmission;
  dentalClaim: ClaimSubmission;
  header: {
    exitButton: {
      label: string;
    };
  };
  footerSection: ClaimSubmissionFooterConfig;
}

export interface ClaimSubmissionInformationConfig {
  medicalClaim: ClaimInformation;
  dentalClaim: ClaimInformation;
  footerSection: ClaimSubmissionInformationFooterConfig;
}

export interface ClaimSubmissionInformationFooterConfig {
  cancel: ButtonConfig;
  continue: ButtonConfig;
}

export interface ClaimSubmissionFooterConfig {
  totalClaimText: string;
  save?: ButtonConfig;
  review: ButtonConfig;
  back: ButtonConfig;
  submit: ButtonConfig;
}

export interface ClaimSubmission {
  claimType: string;
  forms: ClaimSubmissionFormGroup;
  reviews: ClaimSubmissionReview;
  tabs: {
    claimSubmission: TabsData;
    review: TabsData;
  };
  policyId: string;
  claimSubmissionUrl: string;
  submittedClaimReference: SubmittedClaimReference;
}

export interface ClaimSubmissionReview {
  supportingDocumentation?: ClaimSubmissionReviewAttributes;
  patientInformation?: ClaimSubmissionReviewAttributes;
  providerInformation?: ClaimSubmissionReviewAttributes;
  claimLineInformation?: ClaimSubmissionReviewAttributes;
  serviceDetails?: ClaimSubmissionReviewAttributes;
  totalProviderCharges?: ClaimSubmissionReviewAttributes;
  dentalTreatment?: ClaimSubmissionReviewAttributes;
  certification?: ClaimSubmissionReviewAttributes;
}

export interface ClaimSubmissionReviewAttributes {
  position: string;
  supportingDocumentationContent?: ModelAttributeConfig[];
  patientInformationContent?: ModelAttributeConfig[];
  providerInformationContent?: ModelAttributeConfig[];
  claimLineInformationContent?: FormGroupArrayConfig[];
  serviceDetailsContent?: ModelAttributeConfig[];
  dentalTreatmentContent?: ModelAttributeConfig[];
  totalProviderChargesOption?: Option;
  certificationForm?: ClaimSubmissionFormConfig;
  title: string;
}

export interface TabsData {
  position: number;
  tabLabel: ClaimSubmissionTabLabelConfig;
}

export interface ClaimInformation {
  heading: string;
  infoPanelConfig?: InfoPanel;
}

export interface ClaimSubmissionFormGroup {
  supportingDocumentation?: SupportingDocumentation;
  patientInformation?: ClaimSubmissionForm;
  providerInformation?: ClaimSubmissionForm;
  claimLineInformation?: ClaimSubmissionForm;
  serviceDetails?: ClaimSubmissionForm;
}

export interface ClaimSubmissionForm {
  position: number;
  patientInformationForm?: ClaimSubmissionFormConfig;
  providerInformationForm?: ClaimSubmissionFormConfig;
  serviceDetailsForm?: ClaimSubmissionFormConfig;
  description?: string;
  title?: string;
}

export interface ClaimSubmissionFormConfig {
  title?: string;
  description?: string;
  notes?: string;
  controls: AllControlsConfiguration[];
  key?: string;
  submit?: ButtonConfig;
  clear?: ButtonConfig;
  endpoint?: string;
}

export interface SupportingDocumentation {
  position: number;
  showCloseIcon?: boolean;
  addDocumentationButton?: ButtonConfig;
  description?: string;
  title: string;
  errorMessage?: MessageBannerConfig;
  fileUpload?: ButtonConfig;
  fileUploadText?: string;
  fileUploadAlignment?: string;
  documentUploadUrl: string;
  restriction: {
    fileSizeWarning: string;
    description: string;
    title: string;
    fileAttachmentLimit: number;
    fileSizeLimit: number;
    fileTypes: string;
  };
}

export interface ClaimsInstructions {
  body: ClaimInformation;
  footer: ClaimSubmissionInformationFooterConfig;
}

export interface ConfirmationPageFooterConfig {
  viewSubmittedClaim: ButtonConfig;
}

export interface ConfirmationPageContent {
  heading: string;
  details: {
    confirmationNumberDetails: string;
    referenceDetails: string;
    submittedClaimMsg: string;
    claimServicesMsg: string;
  };
}

export interface ConfirmationPageConfig {
  body: ConfirmationPageContent;
  footer: ConfirmationPageFooterConfig;
}

export interface ServiceDetailValues {
  accidentDate: string;
  accidentType: string;
  accidentalInjury: string;
  fault: string;
}
export interface SubmittedClaimReference {
  backButton: ButtonConfig;
  title: string;
  mobileBackIcon: MobileBackIconConfig;
  submittedClaimsUrl: string;
}
export interface MobileBackIconConfig {
  name: string;
  iconConfig: IconConfig;
}

export interface ClaimSubmissionPayload {
  submission_date: string;
  submission_status: string;
  claim_detail: ClaimDetail;
  claim_lines: ClaimLinePayload;
  contact_information: ContactInformation;
  other_insurer_name?: string;
  other_policy_number?: string;
  other_policy_holder_name?: string;
  submission_request_type?: string;
  additional_properties?: AdditionalProperties[];
}

export interface ClaimLinePayload {
  service_from_date: string;
  service_to_date: string;
  billed_amount: string;
  place_of_service_code?: string;
  modifiers?: Modifiers[];
  procedures?: Procedures[];
  diagnoses_information?: DiagnosesInformation[];
  service_units?: number;
  additional_properties?: AdditionalProperties[];
}

export interface ClaimDetail {
  claim_type: string;
  claim_subtype: string;
  policy_id: string;
  member_coverage_id: string;
  patient: PatientInformation;
  providers: Providers[];
  patient_accident_information: PatientAccidentInformation;
  document_ids?: number[];
}

export interface PatientInformation {
  id?: string;
  name?: NameDetails;
}

export interface NameDetails {
  full_name: string;
}

export interface Providers {
  name: string;
  tax_id: number;
  npi: number;
  address: ProviderAddress;
}

export interface ProviderAddress {
  street_name_1: string;
  street_name_2: string;
  city_name: string;
  state: string;
  zip_code: number;
}

export interface PatientAccidentInformation {
  accident_type: string;
  date_of_accident: string;
  fault_person: string;
}

export interface ContactInformation {
  name: NameDetails;
  phone_numbers?: PhoneNumbers[];
}

export interface PhoneNumbers {
  number: number;
  phone_number: number;
  is_primary: true;
}

export interface Modifiers {
  value: number | string;
}

export interface Procedures {
  code: string;
}

export interface DiagnosesInformation {
  diagnosis: {
    code: number | string;
  };
}

export interface AdditionalProperties {
  category?: string;
  property_dictionary: PropertyDictionary;
}

export interface PropertyDictionary {
  area_of_mouth?: string;
  tooth_number_or_letter?: string;
  tooth_surface?: string;
  frequency?: number | string;
  is_orthodontic_treatment_included?: boolean;
  expected_length_of_treatment?: string;
  total_charge_for_active_treatment?: string;
  is_initial_treatment?: boolean;
  appliance_placement_date?: string;
  is_prosthesis_replacement_included?: boolean;
  prosthesis_replacement_reason?: string;
  prosthesis_replacement_reason_description?: string;
}

export interface ClaimLineItem {
  dateServiceFrom: string;
  dateServiceTo: string;
  providerCharge: string;
  placeOfService: string;
  modifierCode: number;
  procedureCode: string;
  diagnosisCode: number;
  units: number;
  areaOfMouth?: string;
  toothNumber?: string;
  toothOfSurface?: string;
  frequency?: number;
}

export interface ProviderInformationContext {
  city: string;
  providerAddressLine1: string;
  providerAddressLine2: string;
  providerName: string;
  providerNpiNumber: string;
  providerTaxId: string;
  state: string;
  zipCode: string;
}

export interface ServiceDetailContext {
  accidentDate: string;
  accidentType: string;
  accidentalInjury: string;
  fault: string;
}

export interface PatientInformationContext {
  contactName: string;
  contactPhoneNumber: string;
  patientName: string;
}

export interface DentalTreatmentContext {
  dateOfAppliancePlaced: string;
  expectedLengthOfTreatment: string;
  initialTreatment: string;
  orthodonticTreatmentIncluded: string;
  otherReasonForReplacement: string;
  reasonForReplacement: string;
  serviceIncludeReplacementOfProsthesis: string;
  totalChargeForActiveTreatment: string;
}

export interface ClaimLineContext {
  fg_array: ClaimLineItemContext[];
}

export interface ClaimLineItemContext {
  dateServiceFrom: string;
  dateServiceTo: string;
  providerCharge?: string;
  placeOfService?: string;
  modifierCode?: string;
  procedureCode?: string;
  diagnosisCode?: string;
  units?: string;
  areaOfMouth?: string;
  toothNumber?: string;
  toothOfSurface?: string;
  frequency?: string;
}
